import { c as _c } from "react/compiler-runtime";
import { differenceInDays, subDays } from 'date-fns';
import { useContext } from 'react';
import useSWR from 'swr';
import { SeekPagination } from '../../../../../../../typings/API.interface';
import { Event } from '../../../../../../../typings/Event.interface';
import { IgnoredEvent } from '../../../../../../../typings/IgnoredEvent.interface';
import { Request } from '../../../../../../../typings/Request.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { RequestListFiltersProps } from '../../../../typings/RequestList.interface';
import { formatDate, getTimezoneAbbreviation } from '../../../../utils/date';
import { cause_filter_breakdown, rejection_cause_labels } from '../../../../utils/rejection-causes';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import Chip from '../../../common/base/Chip';
import Icon from '../../../common/base/Icon';
import Link from '../../../common/base/Link';
import Skeleton from '../../../common/base/Skeleton';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import DisplayDate from '../../../common/DisplayDate';
import { Div } from '../../../common/helpers/StyledUtils';
import JSONBrowser, { DynamicColumnHeader } from '../../../common/JSONBrowser';
import Table, { TableBar } from '../../../common/Table/Table';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { ColumnWidth, parseDynamicColumn, TableContext } from '../../../contexts/TableContext';
import { useNextRelativeDate } from '../../../hooks/useNextRelativeDate';
import usePlansMaxRetention from '../../../hooks/usePlansMaxRetention';
import { useDatePreferences } from '../../../hooks/useDatePreferences';
import { RequestListResult } from '../../../hooks/useRequestList';
import { DashboardContext } from '../DashboardContext';
import RequestListEmptyView from './RequestListEmptyView';
type Pagination = SeekPagination & {
  page_size: number;
  current_page: number;
};
const getNestedValue = (obj: any, path: (string | number)[]): any => {
  if (path.length === 0) return obj;
  const [first, ...rest] = path;
  if (!obj || typeof obj !== 'object') return undefined;
  return getNestedValue(obj[first], rest);
};

// Module-scope helper: the try/catch lives here (outside the component) so the React
// Compiler can memoize RequestsList — it bails on any component whose body contains a
// try/catch statement.
const renderDynamicColumnField = (column_string: string, request: Request) => {
  try {
    const parsed = parseDynamicColumn(column_string);
    let data: any;
    let value: any;
    if (parsed.root === 'path') {
      value = request.data?.path || '/';
    } else if (parsed.root === 'body') {
      const body = request.data?.body;
      data = typeof body === 'string' && parsed.path.length > 0 ? JSON.parse(body) : body || {};
    } else if (parsed.root === 'query') {
      data = request.data?.parsed_query || {};
    } else {
      data = request.data?.headers || {};
    }
    if (parsed.root !== 'path') {
      value = getNestedValue(data, parsed.path);
    }
    const value_string = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (value === undefined || value === null) {
      return <Text muted size="xs">
          -
        </Text>;
    }
    return <Tooltip side="bottom" fill disabled={value_string.length <= 25} delay={500} element={<Div style={{
      maxHeight: '300px',
      width: '300px',
      overflow: 'auto'
    }} p={{
      x: 1
    }}>
            <Text size="xs" semi muted m={{
        b: 1
      }}>
              Payload
            </Text>
            <JSONBrowser json={value} compact />
          </Div>}>
        <Text mono size="xs" ellipsis>
          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
        </Text>
      </Tooltip>;
  } catch (e) {
    return <Text muted size="xs">
        -
      </Text>;
  }
};
interface RequestListProps extends RequestListResult {
  active_filters?: object;
  selected_request_id?: string;
  filters: RequestListFiltersProps;
  selected_entry_id: string;
  total_count?: number;
  pagination: Pagination | undefined;
  entries_list?: {
    entries: (Event | IgnoredEvent)[];
    fetched: boolean;
    has_next: boolean;
    validating: boolean;
  };
  onRequestSelected(id: string | null): void;
  onEntrySelected(id: string): void;
  onPaginationChanged(filters: object): void;
  retry: (request_id: string, webhook_ids?: string[]) => Promise<{
    request: Request;
    events: Event[];
  }>;
  has_new_results?: boolean;
  onRefresh?: () => void;
}
const RequestsList: React.FC<RequestListProps> = t0 => {
  const $ = _c(104);
  const {
    selected_request_id,
    onRequestSelected,
    selected_entry_id,
    onEntrySelected,
    onPaginationChanged,
    filters,
    requests,
    outdated_ids,
    count,
    fetched,
    total_count,
    prev,
    next,
    retry,
    entries_list,
    pagination,
    is_progressive_loading,
    scanned_until,
    has_new_results,
    onRefresh
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription,
    team
  } = useContext(DashboardContext);
  const {
    next_relative_date,
    updateFilters
  } = useNextRelativeDate(filters, subscription);
  const {
    dynamic_columns,
    reorderDynamicColumns,
    updateDynamicColumnWidth
  } = useContext(TableContext);
  const {
    max_retention_days
  } = usePlansMaxRetention();
  let t1;
  let t2;
  let unique_source_ids;
  if ($[0] !== requests) {
    unique_source_ids = requests ? Array.from(new Set(requests.map(_temp))).sort() : [];
    t1 = useSWR;
    t2 = APIMethodKeys.sources.list(unique_source_ids.length > 0 ? {
      id: unique_source_ids
    } : {
      limit: 1
    });
    $[0] = requests;
    $[1] = t1;
    $[2] = t2;
    $[3] = unique_source_ids;
  } else {
    t1 = $[1];
    t2 = $[2];
    unique_source_ids = $[3];
  }
  let t3;
  if ($[4] !== HookdeckAPI.sources || $[5] !== unique_source_ids) {
    t3 = () => HookdeckAPI.sources.list(unique_source_ids.length > 0 ? {
      id: unique_source_ids
    } : {
      limit: 1
    });
    $[4] = HookdeckAPI.sources;
    $[5] = unique_source_ids;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: sources
  } = t1(t2, t3);
  let t4;
  if ($[7] !== sources) {
    t4 = sources && sources.models.reduce(_temp2, {});
    $[7] = sources;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const sources_by_id = t4;
  let t5;
  let t6;
  let unique_webhook_ids;
  if ($[9] !== entries_list) {
    unique_webhook_ids = entries_list?.entries ? Array.from(new Set(entries_list.entries.map(_temp3))).sort() : [];
    t5 = useSWR;
    t6 = unique_webhook_ids.length > 0 && APIMethodKeys.webhooks.list({
      id: unique_webhook_ids
    });
    $[9] = entries_list;
    $[10] = t5;
    $[11] = t6;
    $[12] = unique_webhook_ids;
  } else {
    t5 = $[10];
    t6 = $[11];
    unique_webhook_ids = $[12];
  }
  let t7;
  if ($[13] !== HookdeckAPI.webhooks || $[14] !== unique_webhook_ids) {
    t7 = () => HookdeckAPI.webhooks.list({
      id: unique_webhook_ids
    });
    $[13] = HookdeckAPI.webhooks;
    $[14] = unique_webhook_ids;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const {
    data: webhooks
  } = t5(t6, t7);
  let t8;
  if ($[16] !== webhooks) {
    t8 = webhooks && webhooks.models.reduce(_temp4, {});
    $[16] = webhooks;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const webhooks_by_id = t8;
  let column_order;
  let t9;
  let widths;
  if ($[18] !== dynamic_columns) {
    const base_widths = [{
      min: 180,
      max: 180
    }, {
      min: 232,
      max: 232
    }, {
      min: 200
    }, {
      min: 94,
      max: 94
    }, {
      min: 94,
      max: 94
    }];
    const dynamic_col_width = {
      min: 200,
      max: 200
    };
    const base_headers = ["Request Date", "Status", "Source", "Created Events", "Ignored Events"];
    const all_widths = [...base_widths, ...dynamic_columns.map(column_string => {
      const parsed = parseDynamicColumn(column_string);
      if (parsed.width) {
        return {
          min: parsed.width,
          max: parsed.width
        };
      }
      return dynamic_col_width;
    })];
    const all_headers = [...base_headers, ...dynamic_columns.map(_temp5)];
    let t10;
    bb0: {
      const base_column_count = base_headers.length;
      const dynamic_column_count = dynamic_columns.length;
      if (dynamic_column_count === 0) {
        let t11;
        if ($[22] !== base_column_count) {
          t11 = Array.from({
            length: base_column_count
          }, _temp6);
          $[22] = base_column_count;
          $[23] = t11;
        } else {
          t11 = $[23];
        }
        t10 = t11;
        break bb0;
      }
      let order;
      if ($[24] !== base_column_count || $[25] !== dynamic_column_count) {
        order = [];
        order.push(0, 1);
        for (let i = 0; i < dynamic_column_count; i++) {
          order.push(base_column_count + i);
        }
        for (let i_0 = 2; i_0 < base_column_count; i_0++) {
          order.push(i_0);
        }
        $[24] = base_column_count;
        $[25] = dynamic_column_count;
        $[26] = order;
      } else {
        order = $[26];
      }
      t10 = order;
    }
    column_order = t10;
    const t11 = column_order.map(index_0 => all_widths[index_0]);
    let t12;
    if ($[27] !== t11) {
      t12 = t11.filter(_temp7);
      $[27] = t11;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    widths = t12;
    t9 = column_order.map(index_1 => all_headers[index_1]).filter(_temp8);
    $[18] = dynamic_columns;
    $[19] = column_order;
    $[20] = t9;
    $[21] = widths;
  } else {
    column_order = $[19];
    t9 = $[20];
    widths = $[21];
  }
  const headers = t9;
  let t10;
  bb1: {
    if (dynamic_columns.length === 0) {
      let t11;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = [];
        $[29] = t11;
      } else {
        t11 = $[29];
      }
      t10 = t11;
      break bb1;
    }
    let t11;
    if ($[30] !== dynamic_columns.length) {
      t11 = Array.from({
        length: dynamic_columns.length
      }, _temp9);
      $[30] = dynamic_columns.length;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    t10 = t11;
  }
  const draggable_column_indices = t10;
  const loading = requests === undefined || !fetched || unique_source_ids.length > 0 && sources === undefined;
  const has_request_filter = Object.values(filters.request || {}).some(_temp10);
  if (!loading && requests && count === 0) {
    let t11;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <RequestListEmptyView />;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    return t11;
  }
  let t11;
  if ($[33] !== onEntrySelected || $[34] !== onRequestSelected || $[35] !== selected_request_id) {
    t11 = id => {
      if (selected_request_id === id) {
        onRequestSelected(null);
      }
      return id ? id.indexOf(":") > 0 || id.indexOf("evt_") === 0 ? onEntrySelected(String(id)) : onRequestSelected(String(id)) : onRequestSelected(null);
    };
    $[33] = onEntrySelected;
    $[34] = onRequestSelected;
    $[35] = selected_request_id;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const onRowSelected = t11;
  let t12;
  if ($[37] !== column_order || $[38] !== dynamic_columns || $[39] !== entries_list || $[40] !== loading || $[41] !== outdated_ids || $[42] !== requests || $[43] !== retry || $[44] !== selected_entry_id || $[45] !== selected_request_id || $[46] !== sources_by_id || $[47] !== unique_webhook_ids || $[48] !== webhooks || $[49] !== webhooks_by_id) {
    t12 = loading ? [] : requests.reduce((rows, request_0) => {
      const outdated = outdated_ids.includes(request_0.id);
      const dynamic_fields = dynamic_columns.map(column_string_1 => renderDynamicColumnField(column_string_1, request_0));
      const all_fields = [request_0.created_at && <DisplayDate date={request_0.created_at} show_timezone={false} size="xs" />, request_0.rejection_cause ? <Div flex={{
        gap: 2,
        justify: "space-between",
        align: "center"
      }}><Badge danger={true} subtle={true} icon="error">{rejection_cause_labels[request_0.rejection_cause]}</Badge><Button.Permission role="member" small={true} ghost={true} icon="retry" muted={true} onClick={e => {
          e.stopPropagation();
          retry(request_0.id);
        }} /></Div> : <Badge success={true} subtle={true} icon="check">Accepted</Badge>, sources_by_id?.[request_0.source_id]?.name ? <Chip outline={true} to={`/sources/${request_0.source_id}`} mono={true} label={sources_by_id?.[request_0.source_id]?.name} /> : <Text mono={true} size="xs" muted={true}>Source deleted</Text>, <Div key={`${request_0.id}-events-count`} flex={{
        gap: 2
      }}><Text mono={true} size="xs">{request_0.events_count}</Text>{request_0.cli_events_count > 0 && <Tooltip tooltip="CLI Events. There can be multiple CLI events for a single connection depending on the number of connection clients."><Text muted={true} mono={true} size="xs" flex={{
            align: "center",
            gap: 1
          }}>+{request_0.cli_events_count} <Icon small={true} icon="terminal" /></Text></Tooltip>}</Div>, <Text mono={true} size="xs" key={`${request_0.id}-ignored-count`}>{request_0.ignored_count}</Text>, ...dynamic_fields];
      const ordered_fields = column_order.map(index_2 => all_fields[index_2]);
      return [...rows, {
        id: request_0.id,
        selected: selected_request_id === request_0.id && !selected_entry_id,
        highlighted: selected_request_id === request_0.id,
        has_error: !!request_0.rejection_cause,
        expendable: true,
        outdated,
        fields: ordered_fields
      }, ...(selected_request_id !== request_0.id || request_0.rejection_cause ? [] : [{
        headers: ["Entry Date", "Status", "Affected Connection"],
        rows: !entries_list?.fetched || unique_webhook_ids.length > 0 && webhooks === undefined ? [{
          id: request_0.id,
          selected: false,
          highlighted: false,
          selectable: false,
          expendable: false,
          fields: [<Skeleton h={{
            px: 20
          }} w={{
            px: 140
          }} key="1" />, <Skeleton h={{
            px: 20
          }} w={{
            px: 80
          }} key="2" />, <Skeleton h={{
            px: 20
          }} w={{
            px: 200
          }} key="3" />]
        }] : entries_list.entries.map(entry => ({
          id: entry.id,
          selected: selected_entry_id === String(entry.id),
          highlighted: selected_request_id === request_0.id,
          fields: [<DisplayDate key={entry.id} date={entry.created_at || request_0.created_at} show_timezone={false} size="xs" />, "cause" in entry ? <Div key={entry.id} flex={{
            justify: "space-between",
            gap: 2
          }}><Badge icon="block" neutral={true} mono={true}>{cause_filter_breakdown[entry.cause](entry).label}</Badge><Div flex={{
              align: "center"
            }}><Button.Permission role="member" small={true} ghost={true} icon="retry" muted={true} onClick={e_0 => {
                e_0.stopPropagation();
                retry(request_0.id, [entry.webhook_id]);
              }} /></Div></Div> : <Badge icon="success" neutral={true} mono={true} key={entry.id}>Created Event</Badge>, !webhooks_by_id?.[entry.webhook_id] ? <Text size="s" muted={true} text_wrap={false}>Connection Deleted</Text> : <Chip outline={true} mono={true} label={webhooks_by_id?.[entry.webhook_id]?.full_name} to={`/connections/${entry.webhook_id}`} />]
        }))
      }]), ...(selected_request_id === request_0.id && request_0.events_count === 0 && request_0.cli_events_count === 0 && request_0.ignored_count === 0 && !request_0.rejection_cause ? [{
        id: "cli-offline",
        highlighted: true,
        selectable: false,
        element: <Div p={{
          x: 5,
          y: 1.5
        }} style={{
          gridColumn: "1 / -1"
        }}><Text muted={true} size="s">No events or ignored events associated to this request.</Text></Div>
      }] : []), ...(selected_request_id === request_0.id && entries_list?.entries && entries_list?.has_next && !entries_list?.validating && selected_request_id === request_0.id ? [{
        id: "paging",
        highlighted: true,
        selectable: false,
        element: <Div p={{
          x: 5,
          y: 1.5
        }} style={{
          gridColumn: "1 / -1"
        }}><Text muted={true} size="s" flex={{
            justify: "center",
            gap: 2
          }}>Displaying {entries_list.entries.length} of{" "}{request_0.events_count + (request_0.ignored_count ?? 0) + (request_0.cli_events_count ?? 0)}{" "}<Link small={true} to={`/requests/${request_0.id}`}>View all {"->"}</Link></Text></Div>
      }] : [])];
    }, []);
    $[37] = column_order;
    $[38] = dynamic_columns;
    $[39] = entries_list;
    $[40] = loading;
    $[41] = outdated_ids;
    $[42] = requests;
    $[43] = retry;
    $[44] = selected_entry_id;
    $[45] = selected_request_id;
    $[46] = sources_by_id;
    $[47] = unique_webhook_ids;
    $[48] = webhooks;
    $[49] = webhooks_by_id;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  const rows_0 = t12;
  let t13;
  if ($[51] !== reorderDynamicColumns) {
    t13 = (active_index, over_index) => {
      const dynamic_active_index = active_index - 2;
      const dynamic_over_index = over_index - 2;
      reorderDynamicColumns(dynamic_active_index, dynamic_over_index);
    };
    $[51] = reorderDynamicColumns;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  const handleColumnReorder = t13;
  let t14;
  if ($[53] !== dynamic_columns || $[54] !== updateDynamicColumnWidth) {
    t14 = (visual_index, new_width) => {
      const dynamic_index = visual_index - 2;
      if (dynamic_index >= 0 && dynamic_index < dynamic_columns.length) {
        const field_path = dynamic_columns[dynamic_index].split(":")[0];
        updateDynamicColumnWidth(field_path, new_width);
      }
    };
    $[53] = dynamic_columns;
    $[54] = updateDynamicColumnWidth;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  const handleColumnResize = t14;
  let t15;
  if ($[56] !== filters.date?.max || $[57] !== is_progressive_loading || $[58] !== scanned_until || $[59] !== timezone) {
    t15 = is_progressive_loading && scanned_until ? `Loaded results up to ${formatDate(scanned_until, "yyyy-MM-dd HH:mm:ss", timezone)} ${getTimezoneAbbreviation(timezone, scanned_until)}` : `Loading requests up to ${formatDate(filters.date?.max || new Date(), "yyyy-MM-dd HH:mm", timezone)} ${getTimezoneAbbreviation(timezone, filters.date?.max || new Date())}`;
    $[56] = filters.date?.max;
    $[57] = is_progressive_loading;
    $[58] = scanned_until;
    $[59] = timezone;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  const load_message = t15;
  const t16 = pagination?.current_page || 1;
  const t17 = pagination?.page_size || 100;
  let t18;
  if ($[61] !== t16 || $[62] !== t17) {
    t18 = {
      current_page: t16,
      page_size: t17,
      entity_name: "requests"
    };
    $[61] = t16;
    $[62] = t17;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] !== next || $[65] !== onPaginationChanged) {
    t19 = next ? () => {
      onPaginationChanged({
        next,
        prev: undefined
      });
    } : undefined;
    $[64] = next;
    $[65] = onPaginationChanged;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== onPaginationChanged || $[68] !== prev) {
    t20 = prev ? () => {
      onPaginationChanged({
        prev,
        next: undefined
      });
    } : undefined;
    $[67] = onPaginationChanged;
    $[68] = prev;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== count || $[71] !== is_progressive_loading || $[72] !== load_message || $[73] !== rows_0 || $[74] !== scanned_until) {
    t21 = is_progressive_loading && scanned_until && rows_0.length > 0 ? <TableBar sticky={true} is_loading={true} load_progress={count}><Text semi={true} muted={true} center={true} p={{
        x: 6,
        y: 1
      }}>{load_message}</Text></TableBar> : undefined;
    $[70] = count;
    $[71] = is_progressive_loading;
    $[72] = load_message;
    $[73] = rows_0;
    $[74] = scanned_until;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] !== filters.date?.min || $[77] !== loading || $[78] !== max_retention_days || $[79] !== next || $[80] !== next_relative_date || $[81] !== subscription || $[82] !== team || $[83] !== updateFilters) {
    t22 = !loading && !next && next_relative_date ? <TableBar top={true}><Text muted={true} semi={true} center={true} size="s" p={{
        x: 6,
        y: 1
      }}>You've reached the end of the selected date range.{" "}<Link small={true} onClick={() => updateFilters(next_relative_date.key)}>Expand to {next_relative_date.short_label}</Link></Text></TableBar> : !loading && !next && differenceInDays(new Date(filters.date?.min || team.created_at), subDays(new Date(), subscription.retention_days)) <= 0 ? <TableBar top={true}><Text muted={true} semi={true} center={true} size="s" p={{
        x: 6,
        y: 1
      }}>You've reached the end of your {subscription?.retention_days} days archival window.{max_retention_days && subscription.retention_days < max_retention_days && <Link to="/settings/organization/plans?highlight=retention_days" icon="upgrade" m={{
          l: 2
        }}>Upgrade</Link>}</Text></TableBar> : undefined;
    $[76] = filters.date?.min;
    $[77] = loading;
    $[78] = max_retention_days;
    $[79] = next;
    $[80] = next_relative_date;
    $[81] = subscription;
    $[82] = team;
    $[83] = updateFilters;
    $[84] = t22;
  } else {
    t22 = $[84];
  }
  const t23 = has_request_filter ? undefined : total_count;
  let t24;
  if ($[85] !== count || $[86] !== draggable_column_indices || $[87] !== handleColumnReorder || $[88] !== handleColumnResize || $[89] !== has_new_results || $[90] !== headers || $[91] !== load_message || $[92] !== loading || $[93] !== onRefresh || $[94] !== onRowSelected || $[95] !== rows_0 || $[96] !== t18 || $[97] !== t19 || $[98] !== t20 || $[99] !== t21 || $[100] !== t22 || $[101] !== t23 || $[102] !== widths) {
    t24 = <Table fill={true} footer={false} top_offset={0} headers={headers} widths={widths} rows={rows_0} loading={loading} loading_title={load_message} loading_message={"We\u2019re scanning the selected time range for matching results. If you\u2019d like to find results faster, try a narrower date range."} onRowSelected={onRowSelected} draggable_columns={true} draggable_column_indices={draggable_column_indices} onColumnReorder={handleColumnReorder} onColumnResize={handleColumnResize} pagination={t18} onNextPage={t19} onPreviousPage={t20} sticky_bottom_element={t21} bottom_element={t22} current_count={count} total_count={t23} has_new_results={has_new_results} onRefresh={onRefresh} />;
    $[85] = count;
    $[86] = draggable_column_indices;
    $[87] = handleColumnReorder;
    $[88] = handleColumnResize;
    $[89] = has_new_results;
    $[90] = headers;
    $[91] = load_message;
    $[92] = loading;
    $[93] = onRefresh;
    $[94] = onRowSelected;
    $[95] = rows_0;
    $[96] = t18;
    $[97] = t19;
    $[98] = t20;
    $[99] = t21;
    $[100] = t22;
    $[101] = t23;
    $[102] = widths;
    $[103] = t24;
  } else {
    t24 = $[103];
  }
  return t24;
};
export default RequestsList;
function _temp(request) {
  return request.source_id;
}
function _temp2(object, source) {
  return {
    ...object,
    [source.id]: source
  };
}
function _temp3(entries) {
  return entries.webhook_id;
}
function _temp4(object_0, webhook) {
  return {
    ...object_0,
    [webhook.id]: webhook
  };
}
function _temp5(column_string_0) {
  const parsed_0 = parseDynamicColumn(column_string_0);
  return <DynamicColumnHeader key={column_string_0} full_path={parsed_0.display_name} width={parsed_0.width} />;
}
function _temp6(_, index) {
  return index;
}
function _temp7(w) {
  return w !== undefined;
}
function _temp8(h) {
  return h !== undefined;
}
function _temp9(__0, i_1) {
  return i_1 + 2;
}
function _temp10(v) {
  return !!v;
}